import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SaveIcon from "@mui/icons-material/Save";

const TotalMismatchConfirmModal = ({
  open,
  onClose,
  onConfirm,
  dayWiseTotal,
  bookingTotal,
  loading,
  colors,
}) => {
  const difference = dayWiseTotal - bookingTotal;
  const isIncrease = difference > 0;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          bgcolor: isIncrease
            ? colors.redAccent?.[600] || "#f44336"
            : colors.blueAccent?.[700] || "#1976d2",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <WarningAmberIcon />
        Confirm Booking Total Update
      </DialogTitle>

      <DialogContent sx={{ bgcolor: colors.primary[500], pt: 3 }}>
        <Alert
          severity={isIncrease ? "warning" : "info"}
          icon={isIncrease ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="body2">
            {isIncrease
              ? "The day-wise total is HIGHER than the current booking total."
              : "The day-wise total is LOWER than the current booking total."}
          </Typography>
        </Alert>

        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: colors.primary[400],
            border: `1px solid ${colors.grey?.[700] || "#424242"}`,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                color={colors.grey?.[300] || "text.secondary"}
              >
                Current Booking Total
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="#fff">
                ₹{bookingTotal.toLocaleString("en-IN")}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                color={colors.grey?.[300] || "text.secondary"}
              >
                New Day-wise Total
              </Typography>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  color: isIncrease
                    ? colors.redAccent?.[400] || "#ef5350"
                    : colors.blueAccent?.[400] || "#42a5f5",
                }}
              >
                ₹{dayWiseTotal.toLocaleString("en-IN")}
              </Typography>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: `1px solid ${colors.grey?.[700] || "#424242"}`,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="body2"
                color={colors.grey?.[300] || "text.secondary"}
              >
                Difference
              </Typography>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                  color: isIncrease
                    ? colors.redAccent?.[400] || "#ef5350"
                    : colors.blueAccent?.[400] || "#42a5f5",
                }}
              >
                {isIncrease ? "+" : ""}₹{difference.toLocaleString("en-IN")}
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{ mt: 2, color: colors.grey?.[300] || "text.secondary" }}
        >
          {isIncrease
            ? "Proceeding will update the booking total to the higher day-wise amount. This may affect payment calculations."
            : "Proceeding will update the booking total to the lower day-wise amount. This may result in a refund or credit adjustment."}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: colors.primary[500] }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isIncrease ? "error" : "primary"}
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} /> : <SaveIcon />}
          sx={{ px: 3, fontWeight: "bold" }}
        >
          {loading ? "Saving..." : "Yes, Update Booking Total"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TotalMismatchConfirmModal;